import { ArrowBack, Send } from '@mui/icons-material';
import { Box, Button, Grid, Typography, TextField, Avatar, CircularProgress } from '@mui/material';
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@store/store';
import { clearTicketDetails } from '../redux/ticketSlice';
import { fetchTicketDetails } from '../redux/thunk/ticketThunk';
import dayjs from 'dayjs';
import MuiButton from '@components/Button/MuiButton';

interface StatusStyle {
    text: string;
    bg: string;
}

const TicketDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const { ticketDetails, ticketDetailsLoading, error } = useSelector((state: RootState) => state.tickets);

    const renderStatusBadge = (status: string): any => {
        const statusStyles: Record<string, StatusStyle> = {
          Pending: { text: "#9F7803", bg: "#FFF3CD" },
          Closed: { text: "#374151", bg: "#F3F4F6" },
          Resolved: { text: "#4CAF50", bg: "#DBEFDC" },
          Open: { text: "#2196F3", bg: "#D3EAFD" },
          "In Progress": { text: "#9F7803", bg: "#FFF3CD" },
          Escalated: { text: "#B3261D", bg: "#F0D4D2" },
          "Inspection Pending": { text: "#9F7803", bg: "#FFF3CD" },
          "Visit Scheduled": { text: "#0471A2", bg: "#D4F0FD" },
          "Report Submitted": { text: "#4CAF50", bg: "#DBEFDC" },
          "Recommended to CA": { text: "#2196F3", bg: "#D3EAFD" },
          "Application Rejected": { text: "#B3261D", bg: "#F0D4D2" },
          "Payment Pending": { text: "#B86D00", bg: "#FFEACC" },
          "Payment Received": { text: "#43A047", bg: "#D9ECDA" },
          "Forwarded to Competent Authority": {
            text: "#1976D2",
            bg: "#D1E4F6"
          },
          "Provisional License Issued": { text: "#6FA135", bg: "#E2ECD7" },
          "EOT Requested": { text: "#AB47BC", bg: "#EEDAF2" },
          "EOT Approved": { text: "#6A1B9A", bg: "#E1D1EB" },
          "EOT Rejected": { text: "#E53935", bg: "#FAD7D7" },
          "Clarification Requested": { text: "#FF7043", bg: "#FFE2D9" },
          "Clarification Updated": { text: "#26A69A", bg: "#D4EDEB" },
          "Final License Issued": { text: "#2E7D32", bg: "#D5E5D6" },
          "Application Submitted": { text: "#2196F3", bg: "#D3EAFD" },
          Draft: { text: "#7A7A7A", bg: "#9E9E9E33" },
          "Scrutiny Pending": { text: "#9F7803", bg: "#FFF3CD" },
          "BG Forfeiture Initiated": { text: "#B3261D", bg: "#F0D4D2" }
        };

        const style = statusStyles[status] || { bg: "#f3f4f6", text: "#374151" };

        return (
            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "3px",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    bgcolor: style.bg,
                    color: style.text
                }}
            >
                <Box
                    component="span"
                    sx={{
                        display: "inline-block",
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        bgcolor: style.text
                    }}
                ></Box>
                {status}
            </Box>
        );
    };

    useEffect(() => {
        if (id) {
            dispatch(fetchTicketDetails(id));
        }

        return () => {
            dispatch(clearTicketDetails());
        };
    }, [id, dispatch]);

    if (ticketDetailsLoading) {
        return (
            <Box className="app-details-wrapper">
                <div className="page-content">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                        <CircularProgress />
                    </Box>
                </div>
            </Box>
        );
    }

    if (error || !ticketDetails) {
        return (
            <Box className="app-details-wrapper">
                <div className="page-content">
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/help-desk')}
                        sx={{ color: "#1F2937", fontSize: 14, bgcolor: "transparent", mb: 1, fontWeight: 900 }}
                    >
                        Back to Tickets
                    </Button>
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography color="error">
                            {error
                                ? (typeof error === 'string'
                                    ? error
                                    : (error as any)?.message || 'An error occurred')
                                : 'Ticket not found'}
                        </Typography>
                    </Box>
                </div>
            </Box>
        );
    }

    return (
        <Box className="app-details-wrapper">
            <div className="page-content">
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/help-desk')}
                    sx={{ color: "#1F2937", fontSize: 14, bgcolor: "transparent", mb: 1, fontWeight: 900 }}
                >
                    Back to Tickets
                </Button>
                <>
                    <Grid container spacing={4} mb={2}>
                        <Grid item xs={12} sm={8} >
                            {/* details */}
                            <Box className="w-full bg-gray-000 rounded-lg p-8" mb={3}>
                                <Box mb={2} display={"flex"} gap={2} alignItems="center">
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            color: "#1F2937"
                                        }}
                                    >
                                        {ticketDetails?.ticketNumber || 'N/A'}
                                    </Typography>
                                    {renderStatusBadge(ticketDetails?.status?.name || 'N/A')}
                                </Box>

                                <Typography
                                    variant='h6'
                                    mb={2}
                                >
                                    {ticketDetails?.issueDetails?.issueDetails || 'No description available'}
                                </Typography>

                                <Grid container spacing={2} mb={2}>
                                    <Grid item xs={12} sm={6} >
                                        <Typography
                                            variant='h6'>
                                            Category
                                        </Typography>
                                        <Typography
                                            variant='body1'>
                                            {ticketDetails?.issueType?.name || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} >
                                        <Typography
                                            variant='h6'>
                                            Assigned to
                                        </Typography>
                                        <Typography
                                            variant='body1'>
                                            {ticketDetails?.assignedTo?.name || 'Not assigned'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2} mb={2}>
                                    <Grid item xs={12} sm={6} >
                                        <Typography
                                            variant='h6'>
                                            Created on
                                        </Typography>
                                        <Typography
                                            variant='body1'>
                                            {ticketDetails?.createdAt ? dayjs(ticketDetails.createdAt).format('MMM DD, YYYY [at] h:mm a') : 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} >
                                        <Typography
                                            variant='h6'>
                                            Last Updated
                                        </Typography>
                                        <Typography
                                            variant='body1'>
                                            {ticketDetails?.updatedAt ? dayjs(ticketDetails.updatedAt).format('MMM DD, YYYY [at] h:mm a') : 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            {/* comments & chat */}
                            <Box className="w-full bg-gray-000 rounded-lg p-6">
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        color: "#1F2937",
                                        mb: 3
                                    }}
                                >
                                    Conversation
                                </Typography>

                                {/* Render all threads dynamically */}
                                {ticketDetails?.allThreads?.map((thread: any, index: number) => {
                                    const isUser = index === 0;
                                    const senderName = isUser ? 'You' : ticketDetails?.currentlyWithData?.currentlyWithRoleName || 'Support';

                                    return (
                                        <Box
                                            key={thread.ticketThreadID}
                                            sx={{
                                                mb: 3,
                                                display: 'flex',
                                                flexDirection: isUser ? 'row-reverse' : 'row',
                                                alignItems: 'flex-start',
                                                gap: 1.5
                                            }}
                                        >
                                            {/* Avatar */}
                                            <Avatar
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    fontSize: '1rem',
                                                    bgcolor: isUser ? '#3a1c73' : '#6B7280',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {senderName.substring(0, 1).toUpperCase()}
                                            </Avatar>

                                            {/* Message Content */}
                                            <Box sx={{ flex: 1, maxWidth: '75%' }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        mb: 0.5,
                                                        justifyContent: isUser ? 'flex-end' : 'flex-start'
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: "#1F2937",
                                                            fontSize: "0.875rem"
                                                        }}
                                                    >
                                                        {senderName}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            color: "#6B7280",
                                                            fontSize: "0.75rem"
                                                        }}
                                                    >
                                                        {dayjs(thread.createdAt).format('MMM DD, h:mm a')}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        bgcolor: isUser ? "#3a1c73" : "#F3F4F6",
                                                        color: isUser ? "#FFFFFF" : "#1F2937",
                                                        borderRadius: "12px",
                                                        p: 2,
                                                        wordWrap: 'break-word'
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: "0.875rem",
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        {thread.body || 'No message content'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}

                                {/* Message Input */}
                                {/* <Box sx={{ mt: 4 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder="Write your response here"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                borderColor: '#E5E7EB',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#D1D5DB'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#3a1c73',
                                                    borderWidth: '1px'
                                                }
                                            }
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                        <MuiButton
                                            variant="contained"
                                            sx={{
                                                bgcolor: "#3a1c73",
                                                "&:hover": { bgcolor: "#3a1c73" }
                                            }}
                                            name="Send Message"
                                            endIcon={<Send />}
                                        />
                                    </Box>
                                </Box> */}
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4} >
                            {/* Activity */}
                            <Box className="w-full bg-gray-000 rounded-lg p-8" mb={3}>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#1F2937",
                                            mb: 3
                                        }}
                                    >
                                        Activity Timeline
                                    </Typography>

                                    {/* Render timeline from allThreads */}
                                    {ticketDetails?.allThreads?.map((thread: any, index: number) => {
                                        const isLast = index === ticketDetails.allThreads.length - 1;
                                        const isUser = index === 0;
                                        const actorName = isUser ? 'You' : ticketDetails?.currentlyWithData?.currentlyWithRoleName || 'System';

                                        return (
                                            <Box key={thread.ticketThreadID} sx={{ mb: isLast ? 0 : 3, position: 'relative', pl: 3 }}>
                                                {/* Timeline dot and line */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        top: 0,
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%',
                                                        bgcolor: '#3a1c73',
                                                        border: '2px solid #fff',
                                                        boxShadow: '0 0 0 2px #3a1c73',
                                                        zIndex: 2
                                                    }}
                                                />
                                                {!isLast && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            left: '5px',
                                                            top: '12px',
                                                            bottom: '-20px',
                                                            width: '2px',
                                                            bgcolor: '#E5E7EB',
                                                            zIndex: 1
                                                        }}
                                                    />
                                                )}

                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: "#1F2937",
                                                        fontSize: "0.875rem",
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {thread.threadType}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: "#6B7280",
                                                        fontSize: "0.75rem",
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {dayjs(thread.createdAt).format('DD MMM YYYY, hh:mm A')}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: "#6B7280",
                                                        fontSize: "0.75rem"
                                                    }}
                                                >
                                                    by {actorName}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </>
            </div>
        </Box>
    )
}

export default TicketDetail